import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import founders from "./founders.png";

/* ─── SAME THEME TOKENS AS CONTACT / WAITLIST ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#188038",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#81c995",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root {
    min-height: 100%;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    background: ${t.bg};
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .fade-up { animation: fadeUp 0.25s ease forwards; }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${t.textSub};
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
    flex-shrink: 0;
  }

  .icon-btn:hover {
    background: ${t.surface};
    color: ${t.text};
  }

  .ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${t.border};
    border-radius: 12px;
    background: ${t.bg};
    color: ${t.text};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, border-color 0.12s;
    text-decoration: none;
  }

  .ghost-btn:hover {
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }

  .primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 12px;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    text-decoration: none;
    transition: opacity 0.12s, transform 0.12s;
  }

  .primary-btn:hover {
    opacity: .88;
    transform: translateY(-1px);
  }

  .material-icons-round {
    font-size: 20px;
    user-select: none;
    line-height: 1;
  }

  .footer {
    margin-top: 24px;
    background: ${t.bgSub};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .footer-inner {
    padding: 24px;
  }

  .footer-top {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr 1fr 1fr;
    gap: 24px;
    margin-bottom: 22px;
  }

  .footer-logo {
    font-size: 22px;
    font-weight: 800;
    color: ${t.text};
    margin-bottom: 10px;
  }

  .footer-logo span {
    color: ${t.textSub};
  }

  .footer-brand p {
    margin: 0;
    font-size: 13px;
    color: ${t.textSub};
    line-height: 1.7;
  }

  .footer-col h4 {
    margin: 0 0 14px;
    font-size: 13px;
    font-weight: 700;
    color: ${t.text};
  }

  .footer-col a {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: ${t.textSub};
    text-decoration: none;
    transition: color 0.12s ease;
  }

  .footer-col a:hover {
    color: ${t.text};
  }

  .footer-divider {
    height: 1px;
    background: ${t.border};
    margin-bottom: 16px;
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .footer-bottom span {
    font-size: 12px;
    color: ${t.textMuted};
  }

  @media (max-width: 768px) {
    .footer {
      border-radius: 16px;
    }

    .footer-top {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 520px) {
    .footer-top {
      grid-template-columns: 1fr;
    }
  }
`;

export default function AboutUs() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);

  const T = dark ? THEMES.dark : THEMES.light;

  const values = [
    {
      icon: "psychology",
      title: "Intelligence first",
      desc: "We build on cutting-edge research so our products stay ahead of the curve.",
    },
    {
      icon: "lock",
      title: "Privacy by design",
      desc: "We treat user data with the highest standards of security and transparency.",
    },
    {
      icon: "bolt",
      title: "Speed & reliability",
      desc: "Our infrastructure is built to scale so you never face downtime when it matters.",
    },
    {
      icon: "handshake",
      title: "Developer-friendly",
      desc: "We build APIs and tools that developers love to integrate and extend.",
    },
  ];

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const styleId = "omrali-about-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: T.accent,
          color: T.accentText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          fontFamily: "'Google Sans', sans-serif",
          flexShrink: 0,
        }}
      >
        O
      </div>
      <span
        style={{
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: "'Google Sans', sans-serif",
          letterSpacing: "-0.3px",
        }}
      >
        Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
      </span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
      {/* Header (same as Contact) */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: mobile ? "10px 14px" : "10px 20px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg,
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="icon-btn" onClick={() => navigate(-1)} title="Back">
            <Icon n="arrow_back" />
          </button>
          <Logo />
        </div>

        <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
          <Icon n={dark ? "light_mode" : "dark_mode"} />
        </button>
      </header>

      {/* Main */}
      <main
        style={{
          maxWidth: 1040,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 24px 36px",
        }}
      >
        <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Hero */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "24px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: T.accent,
                  display: "inline-block",
                }}
              />
              About Omrali AI
            </div>

            <h1
              style={{
                fontSize: mobile ? 28 : 38,
                lineHeight: 1.1,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: T.text,
                marginBottom: 12,
              }}
            >
              Building the future of conversational AI
            </h1>

            <p
              style={{
                color: T.textSub,
                fontSize: 14.5,
                lineHeight: 1.8,
                maxWidth: 760,
              }}
            >
              We’re a passionate team crafting next-generation AI platforms that help
              businesses and developers build smarter, more human-like experiences.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
                gap: 12,
                marginTop: 18,
              }}
            >
              {[
                { num: "3", lbl: "Co-founders" },
                { num: "2024", lbl: "Founded" },
                { num: "∞", lbl: "Ambition" },
              ].map((s) => (
                <div
                  key={s.lbl}
                  style={{
                    background: T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    padding: "16px 14px",
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 700, color: T.text }}>{s.num}</div>
                  <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission */}
          <div
            style={{
              background: T.bgSub,
              border: `1px solid ${T.border}`,
              borderRadius: 18,
              padding: mobile ? "16px" : "22px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 10,
              }}
            >
              Our mission
            </div>

            <h2
              style={{
                fontSize: mobile ? 24 : 30,
                fontWeight: 700,
                color: T.text,
                marginBottom: 10,
              }}
            >
              What we do
            </h2>

            <p
              style={{
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.8,
                marginBottom: 16,
              }}
            >
              We build AI products that remove the friction between humans and intelligent systems.
            </p>

            <div
              style={{
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderLeft: `4px solid ${T.accent}`,
                borderRadius: 16,
                padding: mobile ? "16px" : "22px",
              }}
            >
              <p
                style={{
                  color: T.text,
                  fontSize: 14.5,
                  lineHeight: 1.9,
                }}
              >
                At Omrali AI, we build next-generation conversational AI platforms that empower
                businesses and developers to create human-like, intelligent chatbots and automation tools.
                Our platform integrates advanced AI research with scalable cloud infrastructure to deliver
                seamless, reliable, and customizable AI-powered solutions.
              </p>
            </div>
          </div>

          {/* Founders */}
          <div
            style={{
              background: T.bgSub,
              border: `1px solid ${T.border}`,
              borderRadius: 18,
              padding: mobile ? "16px" : "22px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 10,
              }}
            >
              The team
            </div>

            <h2
              style={{
                fontSize: mobile ? 24 : 30,
                fontWeight: 700,
                color: T.text,
                marginBottom: 10,
              }}
            >
              Meet our founders
            </h2>

            <p
              style={{
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.8,
                marginBottom: 18,
              }}
            >
              Omrali AI was created by a passionate team dedicated to advancing
              conversational AI technology for everyone.
            </p>

            <div
              style={{
                borderRadius: 18,
                overflow: "hidden",
                border: `1px solid ${T.border}`,
                background: T.bg,
              }}
            >
              <img
                src={founders}
                alt="Omrali AI Founders"
                style={{
                  width: "100%",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 16,
              }}
            >
              {["Rayyan Amanulla", "Omesh N", "Liron Dsouza"].map((name) => (
                <span
                  key={name}
                  style={{
                    background: T.bg,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                    fontWeight: 600,
                    fontSize: 13,
                    padding: "8px 14px",
                    borderRadius: 999,
                  }}
                >
                  {name}
                </span>
              ))}
            </div>

            <p
              style={{
                textAlign: "center",
                marginTop: 12,
                fontSize: 13,
                color: T.textSub,
              }}
            >
              Founding team of Omrali AI
            </p>
          </div>

          {/* Values */}
          <div
            style={{
              background: T.bgSub,
              border: `1px solid ${T.border}`,
              borderRadius: 18,
              padding: mobile ? "16px" : "22px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 10,
              }}
            >
              What we stand for
            </div>

            <h2
              style={{
                fontSize: mobile ? 24 : 30,
                fontWeight: 700,
                color: T.text,
                marginBottom: 10,
              }}
            >
              Our values
            </h2>

            <p
              style={{
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.8,
                marginBottom: 18,
              }}
            >
              The principles that guide every product decision we make.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "repeat(2, 1fr)",
                gap: 14,
              }}
            >
              {values.map((v) => (
                <div
                  key={v.title}
                  style={{
                    background: T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: 16,
                    padding: "18px",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: T.aiBubble,
                      border: `1px solid ${T.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 12,
                    }}
                  >
                    <Icon
                      n={v.icon}
                      style={{
                        fontSize: 22,
                        color: T.text,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: T.text,
                      marginBottom: 6,
                    }}
                  >
                    {v.title}
                  </div>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: T.textSub,
                      lineHeight: 1.7,
                    }}
                  >
                    {v.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "22px",
              boxShadow: T.shadow,
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: mobile ? 22 : 28,
                fontWeight: 700,
                color: T.text,
                marginBottom: 10,
              }}
            >
              Want early access?
            </h2>

            <p
              style={{
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.7,
                maxWidth: 700,
                margin: "0 auto 18px",
              }}
            >
              Join the waitlist to get product updates, launch announcements, and early access to Omrali AI.
            </p>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <a href="/waitlist" className="primary-btn">
                <Icon n="mail" />
                Join Waitlist
              </a>

              <a href="/contact" className="ghost-btn">
                <Icon n="send" />
                Contact Us
              </a>
            </div>
          </div>

          {/* Footer (same as Contact) */}
          <div className="footer fade-up">
            <div className="footer-inner">
              <div className="footer-top">
                <div className="footer-brand">
                  <div className="footer-logo">
                    Omrali <span>AI</span>
                  </div>
                  <p>
                    Next-generation conversational AI for businesses and developers.
                    Built with care, designed to scale.
                  </p>
                </div>

                <div className="footer-col">
                  <h4>Product</h4>
                  <a href="https://chat.omrali.com">Chat</a>
                </div>

                <div className="footer-col">
                  <h4>Company</h4>
                  <a href="/about">About us</a>
                  <a href="/waitlist">Beta</a>
                  <a href="/contact">Contact</a>
                </div>

                <div className="footer-col">
                  <h4>Legal</h4>
                  <a href="/privacy">Privacy policy</a>
                  <a href="/terms">Terms of service</a>
                </div>

                <div className="footer-col">
                  <h4>Social</h4>
                  <a href="https://www.instagram.com/omrali.ai/">Instagram</a>
                  <a href="https://twitter.com/omrali_ai">Twitter</a>
                </div>
              </div>

              <div className="footer-divider" />

              <div className="footer-bottom">
                <span>© 2026 Omrali AI. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}